import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePrioStyles } from '../../../../theme/utils';
import Flex from '../../../../components/Flex';
import {
  Typography,
  Tooltip,
  Divider,
  Select,
  Tabs,
  notification,
  Popover,
  Form,
} from 'antd';
import { Button } from '@prio365/prio365-react-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import {
  CopyToProjectInformation,
  FlagStatus,
  Message,
  MessageAttachment,
  MessageCenterMessage,
  MessageRecipient,
} from '../../../../models/Message';
import { downloadAttachment } from '../EmailComposer/EmailComposer';
import {
  ContactId,
  MailCategoryColorName,
  ProjectId,
} from '../../../../models/Types';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { DriveItem } from '../../../../models/Drive';
import {
  getAddedContacts,
  getCurrentFolderItem,
  getMailSettings,
  getMessageCategoryColorMap,
  getProject,
  getWidgetAreaActiveProjectId,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { Project } from '../../../../models/Project';
import { useDispatch, useSelector } from 'react-redux';
import MailCategoriesSelector from '../Categories/MailCategoriesSelector';
import { CreateTaskRequest } from '../../../../models/Task';
import TasksDrawer from '../../../tasks/components/TasksDrawer';
import {
  CopyableTextTile,
  copyToClipboard,
} from '../../../../components/CopyableTextTile';
import { CustomTagProps } from 'rc-select/lib/interface/generator';
import PrioSpinner from '../../../../components/PrioSpinner';
import { html2text } from '../../util';
import { longDateFormatString } from '../../../../util';
import i18n from '../../../../i18n';
import MessageAttachmentList from '../MessageAttachmentList';
import MessageDisplayMainContent, {
  HtmlTextContentRef,
} from './HtmlTextContent';
import QuickActionButtonRow from './QuickActionButtonRow';
import DraftListContainer from './DraftListContainer';
import classNames from 'classnames';
import { flagMessage } from '../../actions/actionControllers/messageActionController';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../../theme/types';
import { Contact } from '../../../../models/Contact';
import UserAvatar from '../../../../components/UserAvatar';
import useContactsContext from '../../../contacts/hooks/useContactsProvider';
import { CopyInformationPanel } from './CopyInformationPanel';
import { SentFromProjectPanel } from './SentFromProjectPanel';
import { apiCheckProjectContact } from '../../../projects/api';
import { CheckProjectContact } from '../../../../models/ProjectContacts';
import { openContactsDrawer } from '../../../contacts/actions/drawer';
import { useContactAIExtraction } from '../../../contacts/hooks/useContactAIExtraction';
import { parseVcf, decodeWithFallback } from '../../../contacts/utils/parseVcf';
import { apiDownloadAttachmentToBase64 } from '../../api';
import { resolveCompanyId } from '../../../contacts/utils/resolveCompany';
import AddExternalContactDrawer from '../../../contacts/components/AddExternalContactDrawer';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { createExternalProjectContact } from '../../../projects/actions';
import { setDocumentsMetaState } from '../../../documents/actions/meta';
import { DriveItemsFetchContext } from '../../../documents/actions';
import AssignMessageModal from './AssignMessageModal';
import IcsImportModal from './IcsImportModal';
import { apiIcsPreview, IcsImportPreviewResponse } from '../../../calendar/api';
import MailScrollContainer, {
  MailScrollContainerRef,
} from '../MailScrollContainer';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    maxHeight: '100%',
    '& .ant-tabs > .ant-tabs-nav .ant-tabs-nav-more': {
      padding: '0 16px',
    },
  },
  tabsViewCompressedView: {
    paddingTop: '0px !important',
    padding: '12px !important',
  },
  tabs: {
    padding: theme.old.spacing.defaultPadding,
    paddingTop: 0,
    flex: 1,
    overflow: 'hidden',
    '& .ant-tabs-content-holder': {
      boxShadow: theme.old.palette.boxShadow.regular,
      backgroundColor: theme.old.palette.backgroundPalette.content,
      flex: 1,
      overflow: 'hidden',
    },
    '& .ant-tabs-nav-wrap': {
      background: 'transparent',
      flex: 1,
    },
    '& .ant-tabs-tab': {
      flex: 'unset',
      maxWidth: 'unset',
      minWidth: 'unset',
    },
    '& .ant-tabs-extra-content:first-child': {
      overflow: 'hidden',
      flex: 'unset',
      marginRight: theme.old.spacing.unit(1.5),
    },
    '& .prio-mail-tab-invisible': {
      display: 'none',
    },
  },
  singleTab: {
    '& .ant-tabs-nav-wrap': {
      visibility: 'hidden',
    },
  },
  tabsEvent: {
    paddingTop: theme.old.spacing.defaultPadding,
  },
  content: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    // overflow: 'hidden',
  },
  header: {
    borderBottom: '1px solid #cbcbcb',
    backgroundColor: theme.old.palette.backgroundPalette.content,
    padding: `${theme.old.spacing.defaultPadding}px ${
      theme.old.spacing.defaultPadding
    }px ${theme.old.spacing.unit(2.5)}px`,
    '& .ant-select-multiple .ant-select-selection-item': {
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'none',
      height: '1.5em',
    },
  },
  headerCompressedView: {
    padding: theme.old.spacing.unit(2),
  },

  whiteSurface: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    overflow: 'hidden',
    position: 'relative',
  },
  primaryColorIcon: {
    color: `${theme.old.palette.primaryColor}!important`,
  },
  addressBarLabelCol: {
    width: theme.old.spacing.unit(4),
    fontSize: theme.old.components.mailFolderItem.fontSize,
    fontWeight: theme.old.typography.fontWeight.bold,
    marginRight: theme.old.spacing.unit(1),
    height: '1.5em',
  },
  addressBarContentCol: {
    width: `calc(100% - ${theme.old.spacing.unit(6)}px - 30px)`,
  },
  flexRow: {
    display: 'flex',
    '&:hover': {
      '& $copyAllButtonInvisible': {
        visibility: 'visible',
      },
    },
  },
  copyAllButtonInvisible: {
    cursor: 'pointer',
    visibility: 'hidden',
    height: 14,
    width: 14,
    fontSize: 14,
    color: theme.old.typography.colors.muted,
  },
  categorySelector: {
    width: '100%',
    '& .ant-select': {
      fontSize: theme.old.components.mailFolderItem.fontSize,
    },
    '& .ant-select-multiple .ant-select-selector': {
      padding: 0,
      border: 'none',
    },
  },
  displayAllAttachmentButton: {
    '&.prio-btn': {
      backgroundColor: 'transparent',
      color: theme.colors.base.primary.default,
      paddingLeft: 17,
      '&:hover': {
        backgroundColor: 'transparent',
        color: theme.colors.base.primary.dark,
        '& > .prio-button-icon': {
          color: theme.colors.base.primary.dark,
        },
      },
    },
  },
  attachments: {
    overflowY: 'scroll',
    display: 'flex',
    padding: `${theme.old.spacing.unit(1)}px ${theme.old.spacing.unit(
      1
    )}px ${theme.old.spacing.unit(2)}px`,
    '& > div': {
      width: '50%',
      overflow: 'hidden',
    },
  },
  fullContent: {
    width: '100%',
    overflow: 'hidden',
    margin: 'auto 0',
  },
  fullContentInner: {
    paddingLeft: theme.old.spacing.defaultPadding,
    padding: theme.old.spacing.baseSpacing,
  },
  fullContentInnerSpan: {
    margin: 'auto 0',
    paddingLeft: theme.old.spacing.defaultPadding,
  },
  attachmentDivider: {
    margin: 0,
    borderTopColor: '#cbcbcb',
  },
  whiteOverlay: {
    backgroundColor: theme.old.palette.backgroundPalette.alpha.content,
    height: '100%',
    width: '100%',
    position: 'absolute',
    zIndex: 1,
  },
  addresses: {
    width: '100%',
    '&.ant-select': {
      fontSize: theme.old.components.mailFolderItem.fontSize,
    },
    '& .ant-select-selection-search': {
      width: '0!important',
      overflow: 'hidden',
    },
    '& .ant-select-selector': {
      cursor: 'default!important',
      border: 'none',
    },
    '&.ant-select:not(.ant-select-customize-input) .ant-select-selector': {
      border: 'none',
    },
    '&.ant-select-multiple .ant-select-selector': {
      padding: 0,
    },
    '&.ant-select-multiple .ant-select-selector::after': {
      lineHeight: '1.5em',
      height: 0,
    },
    '& .ant-select-arrow': {
      top: theme.old.spacing.unit(1.5),
      right: 0,
      height: theme.old.spacing.unit(3),
      width: theme.old.spacing.unit(3),
    },
    '& .ant-select-selection-overflow-item-rest > span': {
      cursor: 'pointer',
      margin: 0,
      padding: 0,
      '-webkit-margin-end': 0,
      '-webkit-padding-start': 0,
      '-webkit-padding-end': 0,
      paddingInlineStart: 0,
      paddingInlineEnd: 0,
    },
    '& .ant-select-selection-overflow-item-rest > span > span': {
      margin: 0,
    },
    '& .ant-select-selection-overflow-item': {
      maxWidth: '250px',
      height: '1.5em',
      '& .ant-select-selection-item-content::after': {
        content: '";"',
      },
      '&:nth-last-child(2) .ant-select-selection-item-content::after': {
        content: '""',
      },
    },
  },
  fromRow: {
    height: '23px',
  },
  copyIconBox: {
    padding: theme.old.spacing.baseSpacing,
    paddingRight: theme.old.spacing.unit(1.5),
    paddingLeft: theme.old.spacing.unit(1.5),
  },
  copyContent: {
    padding: theme.old.spacing.unit(0.5),
    paddingLeft: theme.old.spacing.unit(1.5),
    flex: 1,
  },
  copyableTextTile: {
    width: '100%',
  },
  copyContainerTags: {
    width: '100%',
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover.content,
    },
  },
  copyContainerTagsTooltip: {
    '&:hover': {
      backgroundColor: '#00000080',
    },
  },
  copyIconBoxTags: {
    backgroundColor: theme.old.palette.backgroundPalette.hover.sub,
    padding: `0 ${theme.old.spacing.unit(2)}px`,
  },
  copyIconBoxTagsTooltip: {
    backgroundColor: '#000000bf',
    padding: `0 ${theme.old.spacing.unit(2)}px`,
  },
  copyValueTextTags: {
    wordBreak: 'break-all',
    color: theme.old.typography.colors.base,
  },
  copyValueTextTagsTooltip: {
    wordBreak: 'break-all',
    color: theme.old.typography.colors.inverted,
  },
  selectionRest: {
    fontWeight: theme.old.typography.fontWeight.bold,
  },
  selectButton: {
    width: '100%',
    borderTop: theme.old.borders.content,
    '&:hover': {
      borderTop: theme.old.borders.content,
    },
    paddingTop: 4,
    height: 32,
  },
  timeContainer: {
    display: 'flex',
    marginRight: 'auto',
    width: '100%',
    '& > span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  time: {
    fontWeight: theme.old.typography.fontWeight.regular,
    color: theme.old.typography.colors.muted,
    fontSize: theme.old.typography.fontSize.label,
    marginBottom: '0.5em',
    paddingTop: theme.old.spacing.unit(1),
  },
  quickActionBar: {
    marginLeft: theme.old.spacing.unit(1.5),
    paddingRight: theme.old.spacing.unit(1.5),
  },
  quickActionBarCompressed: {
    fontSize: theme.old.typography.fontSize.label,
  },
  title: {
    padding: theme.old.spacing.defaultPadding,
    paddingBottom: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&.ant-typography': {
      margin: '0px',
    },
  },
  titleViewCompressed: {
    '&.ant-typography': {
      paddingTop: 8,
      paddingLeft: 12,
      marginBottom: 0,
    },
  },
  titleNoSubject: {
    padding: theme.old.spacing.defaultPadding,
    paddingBottom: 0,
    paddingRight: theme.old.spacing.unit(1),
  },
  tabTitleCompressed: {
    fontSize: theme.old.typography.fontSize.label,
  },
  scrollbarContainer: {
    overflow: 'auto hidden',
    height: 4,
  },
  checkprojectContactIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

interface MessageDisplayProps {
  className?: string;
  message: Message;
  onDelete: (message: Message) => void;
  onReply: () => Promise<void>;
  onReplyAll: () => Promise<void>;
  onForward: () => Promise<void>;
  onMarkRead: () => Promise<void>;
  onPrintEmail?: () => Promise<void>;
  projectId: ProjectId;
  messageAttachments?: MessageAttachment[];
  onOpenInNewWindow?: VoidFunction;
  showActions?: boolean;
  searchKeywords?: string | null;
  openInNewWindow?: boolean;
  isFetching?: boolean;
  disableTitleBar?: boolean;
  messageAttachmentsLoaded?: boolean;
  draftMessages: Message[];
  isEvent?: boolean;
}

export const MessageDisplay: React.FC<MessageDisplayProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const {
    className,
    message,
    onDelete,
    onReply,
    onReplyAll,
    onForward,
    onMarkRead,
    onPrintEmail,
    messageAttachments: messageAttachmentsFromProps,
    projectId,
    draftMessages,
    onOpenInNewWindow,
    showActions,
    searchKeywords,
    openInNewWindow,
    isFetching,
    disableTitleBar,
    messageAttachmentsLoaded,
    isEvent,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getContactById } = useContactsContext();
  const [form] = Form.useForm();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const ref = useRef(null);

  const scrollableRef = useRef<MailScrollContainerRef>(null);

  const htmlContentRef = useRef<HtmlTextContentRef>(null);

  const isMe = projectId === 'me';

  const [selectedMessageAttachmentIds, setSelectedMessageAttachmentIds] =
    useState<string[]>([]);

  const [assignMessageModalVisible, setAssignMessageModalVisible] =
    useState<boolean>(false);

  const [icsImportModalVisible, setIcsImportModalVisible] =
    useState<boolean>(false);
  const [icsPreviewData, setIcsPreviewData] =
    useState<IcsImportPreviewResponse | null>(null);
  const [icsProjectPreviewData, setIcsProjectPreviewData] =
    useState<IcsImportPreviewResponse | null>(null);

  const [assignedToId, setAssignedToId] = useState<ContactId>(
    message.customSingleValueExtendedProperties?.messageAssignmentId ?? null
  );
  const assignedTo = useMemo(() => {
    return getContactById(assignedToId);
  }, [assignedToId, getContactById]);

  const widgetAreaProjectId = useSelector(getWidgetAreaActiveProjectId);

  const isProjectProjectId = useMemo(() => {
    return projectId !== 'me' && projectId !== 'favorites';
  }, [projectId]);

  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, isProjectProjectId ? projectId : widgetAreaProjectId)
  );

  const groupId = project?.groupId;

  const driveItem = useSelector<RootReducerState, DriveItem>((state) =>
    getCurrentFolderItem(state, `root-group-${groupId}`)
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const colorMap = useSelector<
    RootReducerState,
    { [displayName: string]: MailCategoryColorName }
  >((state) => getMessageCategoryColorMap(state, projectId));
  const addedContacts = useSelector(getAddedContacts);

  const messageAttachments = (messageAttachmentsFromProps ?? []).filter(
    ({ isInline, name }) =>
      !isInline &&
      !name.match(/^inline-img-/) &&
      !name.match(/\.(p7m)$/) &&
      !name.match(/\.(p7s)$/)
  );

  const messageAttachmentsIncludingHiddenAttachments =
    messageAttachmentsFromProps ?? [];

  const hiddenAttachmentsAvailable =
    messageAttachmentsIncludingHiddenAttachments?.length !==
    messageAttachments?.length;

  const selectedMessageAttachments = messageAttachments.filter((attachment) =>
    selectedMessageAttachmentIds.includes(attachment.id)
  );

  const [task, setTask] = useState<CreateTaskRequest>(null);
  const [attachmentsCollapsed, setAttachmentCollapsed] =
    useState<boolean>(true);
  const [checkProjectContact, setCheckProjectContact] =
    useState<CheckProjectContact>(null);

  const [externalContactDrawerVisible, setExternalContactDrawerVisible] =
    useState<boolean>(false);

  const mailSettings = useSelector(getMailSettings);

  const { extractContactFromEmail, isExtracting } = useContactAIExtraction();

  const copyToProjects = useMemo(() => {
    return (
      (
        JSON.parse(
          message.customSingleValueExtendedProperties?.copiedToProject
        ) as CopyToProjectInformation[]
      )?.filter((info) => info.projectId) ?? []
    );
  }, [message.customSingleValueExtendedProperties?.copiedToProject]);

  const copyBy = getContactById(
    message.customSingleValueExtendedProperties?.copiedBy
  );

  const iconMap = {
    userPlus: { icon: 'user-plus', color: 'red' },
    userTag: { icon: 'user-tag', color: 'orange' },
    userCheck: { icon: 'user-check', color: 'green' },
  };

  const iconKey = !checkProjectContact?.contactExists
    ? 'userPlus'
    : !checkProjectContact?.projectContactExists &&
        checkProjectContact?.isExternal
      ? 'userTag'
      : 'userCheck';
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const openDriveItemConflictModal = () => {
    dispatch(
      setDocumentsMetaState({
        driveItemConflictModalState: {
          behavior: 'copy',
          context: DriveItemsFetchContext.DocumentsPage,
          modelType: 'messageAttachment',
          sourceProjectId: projectId,
          destinationProjectId: !isProjectProjectId
            ? widgetAreaProjectId
            : projectId,
          selectedAttachments: selectedMessageAttachments,
          mailId: message.id,
          destinationFolderId: driveItem?.id,
        },
        showDriveItemConflictModalState: 'widget',
      })
    );
  };

  const onToggleFlag = async (message: Message) => {
    let flagStatus: FlagStatus;
    const messageProjectId =
      projectId === 'favorites'
        ? (message as MessageCenterMessage).projectId
        : projectId;
    switch (message.flag.flagStatus) {
      case 'notFlagged':
        flagStatus = 'flagged';
        break;
      case 'flagged':
        flagStatus = 'complete';
        break;
      case 'complete':
        flagStatus = 'flagged';
        break;
    }
    dispatch(
      flagMessage(
        messageProjectId,
        [{ messageId: message.id, flag: { flagStatus } }],
        [{ messageId: message.id, flag: message.flag }]
      )
    );
  };

  const handleFlag = async () => {
    await onToggleFlag(message);
    await new Promise((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  const onAttachmentSelectionChanged = (e: CheckboxChangeEvent) => {
    const attachmentId = e.target.id;
    if (e.target.checked) {
      setSelectedMessageAttachmentIds([
        ...selectedMessageAttachmentIds,
        attachmentId,
      ]);
    } else {
      setSelectedMessageAttachmentIds(
        selectedMessageAttachmentIds.filter((id) => id !== attachmentId)
      );
    }
  };

  const onSelectionChangeAll = (values: string[]) => {
    setSelectedMessageAttachmentIds(values);
  };

  const onDownloadAttachment = async (messageAttachment: MessageAttachment) => {
    if (messageAttachment?.contentBytes?.length > 0) {
      const linkSource = `data:${messageAttachment.contentType};base64,${messageAttachment.contentBytes}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = messageAttachment.name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } else {
      downloadAttachment(projectId, message.id, messageAttachment, t);
    }
  };

  const onDownloadAll = () => {
    const _attachments = hiddenAttachmentsAvailable
      ? messageAttachmentsIncludingHiddenAttachments
      : messageAttachments;

    selectedMessageAttachmentIds?.forEach((id) => {
      let attachment = _attachments.find((attachment) => attachment.id === id);
      if (attachment?.contentBytes?.length > 0) {
        const linkSource = `data:${attachment.contentType};base64,${attachment.contentBytes}`;
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = attachment.name;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      } else {
        downloadAttachment(projectId, message.id, attachment, t);
      }
    });
  };

  const onImportVcfContact = async (messageAttachment: MessageAttachment) => {
    try {
      let vcfText: string;

      if (messageAttachment.contentBytes) {
        const binaryString = atob(messageAttachment.contentBytes);
        const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
        vcfText = decodeWithFallback(bytes);
      } else {
        const blobUrl = await apiDownloadAttachmentToBase64(
          projectId,
          message.id,
          messageAttachment
        );
        if (!blobUrl) return;
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        vcfText = decodeWithFallback(bytes);
        URL.revokeObjectURL(blobUrl);
      }

      if (!vcfText) return;

      const {
        contact: vcfContact,
        companyInitialValues,
        companyName,
      } = parseVcf(vcfText);

      const contactData = {
        eMail: vcfContact.eMail || message.from?.emailAddress?.address || '',
        ...vcfContact,
      } as Contact;

      // Resolve company by name (same logic as AI extraction)
      let resolvedCompanyInitialValues = companyInitialValues;
      if (companyName) {
        const companyId = await resolveCompanyId(companyName);
        if (companyId) {
          contactData.companyId = companyId;
          resolvedCompanyInitialValues = undefined;
        }
      }

      dispatch(
        openContactsDrawer({
          view: 'contactNew',
          changedContact: {
            contact: contactData,
            state: 'created',
          },
          email: contactData.eMail,
          companyInitialValues: resolvedCompanyInitialValues,
          emailOrigin: {
            from: message.from?.emailAddress
              ? `${message.from.emailAddress.name || ''} <${message.from.emailAddress.address}>`
              : '',
            subject: message.subject || '',
            body: message.body?.content || '',
          },
        })
      );
    } catch (error) {
      console.error('VCF import failed:', error);
      dispatch(
        openContactsDrawer({
          view: 'contactNew',
          changedContact: {
            contact: {
              eMail: message.from?.emailAddress?.address,
            } as Contact,
            state: 'created',
          },
          email: message.from?.emailAddress?.address,
        })
      );
    }
  };

  const onImportIcsEvent = async (messageAttachment: MessageAttachment) => {
    try {
      let icsText: string;

      if (messageAttachment.contentBytes) {
        icsText = atob(messageAttachment.contentBytes);
      } else {
        const blobUrl = await apiDownloadAttachmentToBase64(
          projectId,
          message.id,
          messageAttachment
        );
        if (!blobUrl) return;
        const response = await fetch(blobUrl);
        icsText = await response.text();
        URL.revokeObjectURL(blobUrl);
      }

      if (!icsText) return;

      const promises: Promise<any>[] = [apiIcsPreview(icsText)];

      if (!isMe) {
        promises.push(apiIcsPreview(icsText, projectId));
      }

      const [personalResult, projectResult] = await Promise.all(promises);

      setIcsPreviewData(personalResult?.data || null);
      setIcsProjectPreviewData(projectResult?.data || null);
      setIcsImportModalVisible(true);
    } catch (error) {
      console.error('ICS preview failed:', error);
      notification.error({
        message: t('calendar:icsImport.previewError'),
      });
    }
  };

  const createTask = async () => {
    setTask({
      title: message.subject,
      description:
        message.body.contentType === 'html' ||
        message.body.contentType === 'Html' ||
        message.body.contentType === 1
          ? html2text(message.body.content)
          : message.body.content,
      projectId,
    });
  };

  const openAssignMessageModal = async () => {
    setAssignMessageModalVisible(true);
  };

  const handleDisplayAllAttachments = () => {
    setAttachmentCollapsed(!attachmentsCollapsed);
  };

  const handleMessageFromClicked = async () => {
    if (!checkProjectContact?.contactExists) {
      const { contact: extractedContact, companyInitialValues } =
        await extractContactFromEmail(message);

      const contactData = {
        eMail: message.from.emailAddress.address,
        ...(extractedContact ?? {}),
      } as Contact;

      dispatch(
        openContactsDrawer({
          view: 'contactNew',
          changedContact: {
            contact: contactData,
            state: 'created',
          },
          email: message.from.emailAddress.address,
          companyInitialValues,
          emailOrigin: {
            from: message.from?.emailAddress
              ? `${message.from.emailAddress.name || ''} <${message.from.emailAddress.address}>`
              : '',
            subject: message.subject || '',
            body: message.body?.content || '',
          },
        })
      );
    }
    if (
      checkProjectContact?.contactExists &&
      !checkProjectContact?.projectContactExists
    ) {
      setExternalContactDrawerVisible(true);
    }
  };

  const handleAddExternalContact = async (values) => {
    const response = await (dispatch as any)(
      createExternalProjectContact({
        contactId: values.contacts[0].contactId,
        projectId: projectId,
        jobTitle: values.contacts[0].jobTitle,
        notes: values.contacts[0].notes,
      })
    );
    if (response.status >= 200 && response.status < 300) {
      setCheckProjectContact({
        ...checkProjectContact,
        projectContactExists: true,
      });
    } else {
      notification.open({
        message: t('common:error'),
        description: t(
          'contacts:projectContactsPage.addExternalContactDrawer.error'
        ),
      });
    }
    setExternalContactDrawerVisible(false);
  };

  const handleOnScrollWidthChange = useCallback(
    (scrollWidth: number, offsetWidth: number) => {
      if (scrollableRef.current) {
        if (scrollWidth === offsetWidth) {
          scrollableRef.current.disableHorizontalScroll();
        } else {
          scrollableRef.current.enableHorizontalScroll();
        }
      }
    },
    []
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (isMe) return;
    let isMounted = true;
    setTimeout(async () => {
      if (isMounted) {
        const res = await apiCheckProjectContact(
          projectId,
          message.from.emailAddress.address
        );
        if (res.data) {
          setCheckProjectContact(res.data);
        }
        setIsLoading(false);
      }
    }, 2000);
    return () => {
      isMounted = false;
    };
  }, [projectId, message.from.emailAddress.address, isMe]);

  useEffect(() => {
    if (isMe) return;
    if (
      !checkProjectContact?.projectContactExists &&
      !checkProjectContact?.contactExists &&
      addedContacts.length > 0
    ) {
      const senderEmail = message.from.emailAddress.address;
      const senderInAddedContacts = addedContacts.some(
        (contact) =>
          contact.eMail === senderEmail ||
          contact.eMail2 === senderEmail ||
          contact.eMail3 === senderEmail
      );

      if (senderInAddedContacts) {
        setCheckProjectContact({
          ...checkProjectContact,
          contactExists: true,
          projectContactExists: false,
          isExternal: true,
          contactId: addedContacts.find(
            (contact) =>
              contact.eMail === senderEmail ||
              contact.eMail2 === senderEmail ||
              contact.eMail3 === senderEmail
          )?.contactId,
        });
      }
    }
  }, [addedContacts, checkProjectContact, message, isMe]);
  //#endregion

  return (
    <>
      <div className={classNames(classes.root, className)} ref={ref}>
        <Flex.Column>
          {!disableTitleBar && (
            <>
              {(copyToProjects.length > 0 || copyBy) && (
                <CopyInformationPanel
                  copyBy={copyBy}
                  copyToProjects={copyToProjects}
                  isMe={isMe}
                />
              )}
              <SentFromProjectPanel message={message} />
              <Flex.Row>
                <Typography.Title
                  level={3}
                  className={classNames(
                    message.subject === ''
                      ? classes.titleNoSubject
                      : classes.title,
                    {
                      [classes.titleViewCompressed]:
                        mailSettings.mailListSpacing === 'tight',
                    }
                  )}
                  title={`${
                    message.importance === 'high'
                      ? `${t('mail:messageDisplay.highImportance')}! `
                      : ''
                  }${
                    message.subject === ''
                      ? `${t('mail:messageDisplay.noSubject')}`
                      : message.subject
                  }`}
                >
                  {message.importance === 'high' && (
                    <span
                      style={{
                        color: theme.old.palette.chromaticPalette.red,
                      }}
                    >
                      {`${t('mail:messageDisplay.highImportance')}! `}
                    </span>
                  )}
                  <span>
                    {!message.subject
                      ? `${t('mail:messageDisplay.noSubject')}`
                      : message.subject}
                  </span>
                </Typography.Title>
              </Flex.Row>
            </>
          )}
        </Flex.Column>
        <Tabs
          className={classNames(classes.tabs, {
            [classes.singleTab]: draftMessages.length === 0,
            [classes.tabsEvent]: isEvent,
            [classes.tabsViewCompressedView]:
              mailSettings.mailListSpacing === 'tight',
          })}
          defaultActiveKey={'mainContent'}
          type="card"
          tabBarStyle={
            mailSettings.mailListSpacing !== 'full'
              ? {
                  margin: 0,
                  height: 32,
                }
              : { margin: 0 }
          }
          hideAdd
          tabBarExtraContent={{
            left: (
              <Flex.Column flex={1}>
                {' '}
                {(message.receivedDateTime || message.createdDateTime) && (
                  <Flex.Item flex={1} className={classes.timeContainer}>
                    <Typography.Text className={classes.time}>
                      {longDateFormatString(
                        message.receivedDateTime ?? message.createdDateTime,
                        i18n.language
                      )}
                    </Typography.Text>
                  </Flex.Item>
                )}
              </Flex.Column>
            ),
            right: showActions && (
              <Flex.Column justifyContent="center">
                <Flex.Row
                  className={classNames(classes.quickActionBar, {
                    [classes.quickActionBarCompressed]:
                      mailSettings.mailListSpacing !== 'full',
                  })}
                >
                  {showActions && (
                    <QuickActionButtonRow
                      projectId={projectId}
                      message={message}
                      listenerRef={ref}
                      messageAttachments={messageAttachments}
                      selectedMessageAttachmentIds={
                        selectedMessageAttachmentIds
                      }
                      openInNewWindow={openInNewWindow}
                      isProject={!isMe}
                      onDownloadAttachment={onDownloadAttachment}
                      onAttachmentSelectionChanged={
                        onAttachmentSelectionChanged
                      }
                      onOpenInNewWindow={onOpenInNewWindow}
                      onDelete={onDelete}
                      onReply={onReply}
                      onReplyAll={onReplyAll}
                      onForward={onForward}
                      onMarkRead={onMarkRead}
                      onPrintEmail={onPrintEmail}
                      createTask={createTask}
                      handleFlag={handleFlag}
                      assignMessage={openAssignMessageModal}
                    />
                  )}
                </Flex.Row>
              </Flex.Column>
            ),
          }}
        >
          <Tabs.TabPane
            key="mainContent"
            tab={
              <Flex.Row
                alignItems="center"
                childrenGap={theme.old.spacing.baseSpacing}
                className={
                  mailSettings.mailListSpacing !== 'full' &&
                  classes.tabTitleCompressed
                }
              >
                <FontAwesomeIcon icon={['fal', 'envelope-open-text']} />
                <Flex.Item className={'prio-mail-tab-invisible'}>
                  {t('mail:mail')}
                </Flex.Item>
              </Flex.Row>
            }
          >
            <Flex.Column width="100%" height="100%">
              {isFetching && (
                <div className={classes.whiteOverlay}>
                  <div className="prio-flex-center-center prio-flex-column">
                    <PrioSpinner size="large" />
                  </div>
                </div>
              )}
              <Flex.Column
                className={classNames(classes.header, {
                  [classes.headerCompressedView]:
                    mailSettings.mailListSpacing === 'tight',
                })}
              >
                <Flex.Row>
                  <Flex.Column flex={1}>
                    <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
                      <Flex.Column flex={1}>
                        <Flex.Row
                          className={classes.fromRow}
                          childrenGap={theme.old.spacing.unit(1)}
                        >
                          <AddressRow recipients={[message.from]} type="from" />
                          {(isLoading || isExtracting) && !isMe && (
                            <PrioSpinner size="small" />
                          )}
                          {(checkProjectContact?.isExternal ||
                            !checkProjectContact?.contactExists) &&
                            !isLoading &&
                            !isExtracting &&
                            !isMe && (
                              <Tooltip
                                title={
                                  iconKey === 'userPlus'
                                    ? t(
                                        'mail:messageDisplay.checkProjectContact.userPlus'
                                      )
                                    : iconKey === 'userTag'
                                      ? t(
                                          'mail:messageDisplay.checkProjectContact.userTag'
                                        )
                                      : t(
                                          'mail:messageDisplay.checkProjectContact.userCheck'
                                        )
                                }
                                placement="topRight"
                                arrowPointAtCenter
                              >
                                <FontAwesomeIcon
                                  className={classes.checkprojectContactIcon}
                                  icon={[
                                    'fal',
                                    iconMap[iconKey].icon as IconName,
                                  ]}
                                  style={{
                                    marginTop: '5px',
                                    color: iconMap[iconKey]
                                      .color as unknown as string,
                                  }}
                                  onClick={handleMessageFromClicked}
                                />
                              </Tooltip>
                            )}
                        </Flex.Row>
                        <AddressRow
                          recipients={message.toRecipients}
                          type="to"
                        />
                        <AddressRow
                          recipients={message.ccRecipients}
                          type="cc"
                        />
                        <AddressRow
                          recipients={message.bccRecipients}
                          type="bcc"
                        />
                      </Flex.Column>
                    </Flex.Row>
                    <Flex.Row>
                      <MailCategoriesSelector
                        className={classes.categorySelector}
                        message={message}
                        projectId={projectId}
                        colorMap={colorMap}
                        maxCount={3}
                      />
                    </Flex.Row>
                  </Flex.Column>
                  {assignedTo && !isMe && (
                    <Flex.Column>
                      <Popover
                        placement="left"
                        content={t('mail:messageDisplay.messageAssignedTo', {
                          firstName: assignedTo?.firstName,
                          lastName: assignedTo?.lastName,
                        })}
                      >
                        {' '}
                        <UserAvatar
                          size={'medium'}
                          contact={assignedTo}
                        ></UserAvatar>
                      </Popover>
                    </Flex.Column>
                  )}
                </Flex.Row>
              </Flex.Column>
              <Flex.Column
                className={classes.whiteSurface}
                alignItems="flex-start"
              >
                {messageAttachmentsLoaded == null ? (
                  <></>
                ) : messageAttachmentsLoaded ? (
                  <></>
                ) : (
                  <Flex.Column className={classes.fullContent}>
                    <Flex.Row className={classes.fullContentInner}>
                      <PrioSpinner size="large" />{' '}
                      <span className={classes.fullContentInnerSpan}>
                        {t('mail:messageDisplay.loadingAttachments')}
                      </span>
                    </Flex.Row>
                    <Divider className={classes.attachmentDivider}></Divider>
                  </Flex.Column>
                )}
                <MessageAttachmentList
                  selectedMessageAttachmentIds={selectedMessageAttachmentIds}
                  setSelectedMessageAttachmentIds={
                    setSelectedMessageAttachmentIds
                  }
                  messageAttachments={messageAttachments}
                  messageAttachmentsIncludingHiddenAttachments={
                    messageAttachmentsIncludingHiddenAttachments
                  }
                  onDownloadAttachment={onDownloadAttachment}
                  onSelectionChange={onAttachmentSelectionChanged}
                  messageId={message.id}
                  onSelectionChangeAll={onSelectionChangeAll}
                  openDriveItemConflictModal={openDriveItemConflictModal}
                  downloadAll={onDownloadAll}
                  isMe={isMe}
                  attachmentsCollapsed={attachmentsCollapsed}
                  projectId={projectId}
                  receivedMessage={true}
                  onImportVcfContact={onImportVcfContact}
                  onImportIcsEvent={onImportIcsEvent}
                />
                {messageAttachments.length > 6 && (
                  <Button
                    type="link"
                    className={classes.displayAllAttachmentButton}
                    onClick={handleDisplayAllAttachments}
                    iconProp={['fal', attachmentsCollapsed ? 'plus' : 'minus']}
                  >
                    {attachmentsCollapsed
                      ? t('mail:messageDisplay.displayAllAttachments')
                      : t('mail:messageDisplay.closeDisplayAllAttachments')}
                  </Button>
                )}
                {messageAttachmentsLoaded &&
                (messageAttachments.length > 0 ||
                  messageAttachmentsIncludingHiddenAttachments?.length !==
                    messageAttachments?.length) ? (
                  <Divider
                    className={classes.attachmentDivider}
                    style={{
                      marginTop:
                        messageAttachments.length <= 6 ? 12 : undefined,
                    }}
                  ></Divider>
                ) : (
                  <></>
                )}
              </Flex.Column>
              <MailScrollContainer
                style={{ flex: 1, overflow: 'hidden' }}
                ref={scrollableRef}
              >
                <MessageDisplayMainContent
                  className={classes.content}
                  message={message}
                  searchKeywords={searchKeywords}
                  ref={htmlContentRef}
                  projectId={projectId}
                  onScrollWidthChange={handleOnScrollWidthChange}
                />
              </MailScrollContainer>
              {/* <div
                style={{
                  width: '100%',
                  overflow: 'hidden',
                  height: 'auto',
                  backgroundColor: theme.old.palette.backgroundPalette.content,
                }}
              >
                <AutoSizer disableHeight>
                  {({ width }) => {
                    if (scrollWidth <= width) {
                      return null;
                    }
                    return (
                      <div
                        className={classes.scrollbarContainer}
                        style={{ width }}
                        ref={horizontalScrollRef}
                        onScroll={handleOnHorizontalScroll}
                      >
                        <div
                          style={{
                            width: scrollWidth,
                            height: 1,
                          }}
                        />
                      </div>
                    );
                  }}
                </AutoSizer>
              </div> */}
            </Flex.Column>
          </Tabs.TabPane>
          {draftMessages.length > 0 && (
            <Tabs.TabPane
              key="draftMessages"
              tab={
                <Flex.Row
                  alignItems="center"
                  childrenGap={theme.old.spacing.baseSpacing}
                  className={
                    mailSettings.mailListSpacing !== 'full' &&
                    classes.tabTitleCompressed
                  }
                >
                  <FontAwesomeIcon icon={['fal', 'pen']} />
                  <Flex.Item className={'prio-mail-tab-invisible'}>
                    {t('mail:drafts')}
                  </Flex.Item>
                  <Flex.Item>{`(${draftMessages.length})`}</Flex.Item>
                </Flex.Row>
              }
            >
              <DraftListContainer
                projectId={projectId}
                draftMessages={draftMessages}
              />
            </Tabs.TabPane>
          )}
        </Tabs>
      </div>
      <TasksDrawer
        sourceEmailUrl={`${window.REACT_APP_API_REDIRECT_URL}view/${projectId}/message/${message.id}/details`}
        messageId={message.id}
        visible={!!task}
        preSelectedTask={task}
        closeDrawer={() => setTask(null)}
        isMe={isMe}
      />
      {assignMessageModalVisible && (
        <AssignMessageModal
          projectId={projectId}
          assignMessageModalVisible={assignMessageModalVisible}
          setAssignMessageModalVisible={setAssignMessageModalVisible}
          selectedMessages={[message]}
          onFinish={setAssignedToId}
        />
      )}
      {icsImportModalVisible && (
        <IcsImportModal
          visible={icsImportModalVisible}
          onClose={() => setIcsImportModalVisible(false)}
          projectId={projectId}
          previewData={icsPreviewData}
          projectPreviewData={icsProjectPreviewData}
          isMe={isMe}
        />
      )}
      <AddExternalContactDrawer
        projectId={projectId}
        visible={externalContactDrawerVisible}
        onClose={() => {
          setExternalContactDrawerVisible(false);
        }}
        onFinish={handleAddExternalContact}
        contactId={checkProjectContact?.contactId}
        form={form}
      ></AddExternalContactDrawer>
    </>
  );
};

export default MessageDisplay;

interface AddressRowProps {
  recipients: MessageRecipient[];
  type: string;
}

export const AddressRow: React.FC<AddressRowProps> = (props) => {
  const { recipients, type } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();

  const mailSettings = useSelector(getMailSettings);

  const maxTagCount = 5;

  const [open, setOpen] = useState<boolean>(false);

  const [clicked, setClicked] = useState(false);
  const copyCodeToClipboard = () => {
    copyToClipboard(
      recipients.map((recipient) => recipient?.emailAddress?.address).join('; ')
    );
    setClicked(true);
    setTimeout(() => {
      setClicked(false);
      setOpen(false);
    }, 750);
  };

  const copyAllRef = useRef(null);

  return (
    recipients?.length > 0 && (
      <div className={classes.flexRow}>
        <div
          className={classes.addressBarLabelCol}
          style={
            mailSettings?.mailListSpacing !== 'full'
              ? {
                  paddingTop: '0.1em',
                }
              : undefined
          }
        >
          {`${t(`mail:addressBar.types.${type}`)}: `}
        </div>
        <Flex.Row className={classes.addressBarContentCol}>
          <Select
            className={classes.addresses}
            mode="tags"
            open={open}
            value={recipients.map(
              (recipient) => recipient?.emailAddress?.address
            )}
            bordered={false}
            tagRender={(props: CustomTagProps) => {
              return <SelectTag {...props} />;
            }}
            maxTagCount={maxTagCount}
            menuItemSelectedIcon={null}
            searchValue=""
            showSearch={false}
            suffixIcon={null}
            onBlur={() => {
              setOpen(false);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(!open);
            }}
            maxTagPlaceholder={(omittedValues) => (
              <div
                className={classes.selectionRest}
                onClick={() => {
                  setOpen(!open);
                }}
              >
                {'+ ' +
                  t('common:select.omittedPlaceholder', {
                    count: omittedValues.length,
                  })}
              </div>
            )}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Button
                  ref={copyAllRef}
                  className={classes.selectButton}
                  type="link"
                  iconProp={['fal', clicked ? 'check' : 'copy']}
                  // added so that the click is happening before the blur event of the select,
                  // which would close the dropdown. Now the dropdown only closes after the
                  // click event is processed, so the user can see the feedback of the click
                  // (icon change) before the dropdown closes. PrioCL updated needed - coming soon
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={copyCodeToClipboard}
                >
                  {t('mail:addressBar.copyAll.all')}
                </Button>
              </>
            )}
            options={
              recipients.length > 0
                ? recipients.map((recipient) => ({
                    style: {
                      backgroundColor:
                        theme.old.palette.backgroundPalette.content,
                      fontWeight: theme.font.fontWeight.regular,
                    },
                    key: Math.random() * (100000 - 1000) + 1000,
                    value: recipient?.emailAddress?.address,
                    title:
                      recipient?.emailAddress?.name ??
                      recipient?.emailAddress?.address,
                    label: (
                      <CopyableTextTile
                        className={classes.copyableTextTile}
                        valueClassName={classes.copyValueTextTags}
                        containerClassName={classes.copyContainerTags}
                        copyIconBoxClassName={classes.copyIconBoxTags}
                        contentClassName={classes.copyContent}
                        label={
                          recipient?.emailAddress?.name ??
                          recipient?.emailAddress?.address
                        }
                        value={recipient?.emailAddress?.address}
                      />
                    ),
                  }))
                : []
            }
          />
        </Flex.Row>
        {recipients.length > 1 &&
          (type === 'to' || type === 'cc' || type === 'bcc') && (
            <Tooltip overlay={t(`mail:addressBar.copyAll.${type}`)}>
              <div
                ref={copyAllRef}
                className={classes.copyAllButtonInvisible}
                onClick={copyCodeToClipboard}
              >
                <FontAwesomeIcon icon={['fal', clicked ? 'check' : 'copy']} />
              </div>
            </Tooltip>
          )}
      </div>
    )
  );
};
const SelectTag: React.FC<CustomTagProps> = (props: CustomTagProps) => {
  const { label, value } = props;
  const classes = useStyles();
  return (
    <span className={'ant-select-selection-item'}>
      <Tooltip
        title={
          <CopyableTextTile
            valueClassName={classes.copyValueTextTagsTooltip}
            containerClassName={classes.copyContainerTagsTooltip}
            copyIconBoxClassName={classes.copyIconBoxTagsTooltip}
            value={value as string}
          />
        }
        className={'ant-select-selection-item-content'}
      >
        {(label as ReactElement)?.props?.label}
      </Tooltip>
    </span>
  );
};
